import { useCallback, useEffect } from "react";
import { Panel } from "@xyflow/react";
import { useAtom, useSetAtom } from "jotai";
import { Text } from "@zapier/design-system";
import {
  emitUserInteractionEventSetter,
  tourIntroStepAtom,
  showActiveAnnotationNodeSetter,
  tourExitSetter,
} from "@zapier/canvas-state";
import { CloseButton, EXIT_TOUR } from "./CloseButton";
import { NextButton } from "./NextButton";
import { EXIT_INTERACTION_GOAL } from "./AnnotationNode";
import styles from "./AnnotationNode.module.css";

const START_TOUR = "Start tour";

export function TourIntroPanel() {
  const [tourIntroStep, setTourIntroStep] = useAtom(tourIntroStepAtom);
  const showActiveAnnotationNode = useSetAtom(showActiveAnnotationNodeSetter);
  const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventSetter);
  const tourExit = useSetAtom(tourExitSetter);

  useEffect(() => {
    (async () => {
      await emitUserInteractionEvent({
        eventAction: "in_view",
        interactionGoal: "tour loaded",
      });
    })();
  }, [tourIntroStep, emitUserInteractionEvent]);

  const { header, description, totalSteps } = tourIntroStep!;

  const handleOnPress = useCallback(
    (action: "start" | "exit") => {
      if (action === "start") {
        const nextStep = 1;

        emitUserInteractionEvent({
          eventAction: "click",
          interactionGoal: `Tour: clicked ${START_TOUR}`,
          rawText: START_TOUR,
          // Show completion based on where we're going
          optionSelected: `completion: ${(nextStep / totalSteps).toFixed(2)}`,
        });

        setTourIntroStep(null);
        showActiveAnnotationNode(nextStep);
      }

      if (action === "exit") {
        emitUserInteractionEvent({
          eventAction: "click",
          interactionGoal: EXIT_INTERACTION_GOAL,
          rawText: EXIT_TOUR,
          optionSelected: "completion: 0.00",
        });

        tourExit(); // No parentId needed for tour exit
      }
    },
    [
      emitUserInteractionEvent,
      totalSteps,
      setTourIntroStep,
      showActiveAnnotationNode,
      tourExit,
    ],
  );

  return (
    <Panel position="top-left">
      <div className={styles.root}>
        <div className={styles.closeButton}>
          <CloseButton onPress={() => handleOnPress("exit")} />
        </div>
        <div className={styles.textSection}>
          <Text type="Title" color="TextDefault">
            {header}
          </Text>
          <Text type="MinimalPrint1Multiline" color="TextWeaker">
            {description}
          </Text>
        </div>
        <div className={styles.footer}>
          <div className={styles.mainButtonWrapper}>
            <NextButton onPress={() => handleOnPress("start")}>
              {START_TOUR}
            </NextButton>
          </div>
        </div>
      </div>
    </Panel>
  );
}
